export const BASEMAP = {
  OSM: 'osm',
  CARTO_LIGHT: 'carto-light',
} as const;

export type TBasemap = (typeof BASEMAP)[keyof typeof BASEMAP];

export type TBasemapTileLayer = {
  url: string;
  attribution: string;
  attributionText: string;
  /** Leaflet reads `subdomains.length` on every tile, even when the template has no `{s}`. */
  subdomains: string;
  maxZoom: number;
  supportsRetina: boolean;
};

/** Leaflet's own default, kept for templates that do not use `{s}`. */
const DEFAULT_SUBDOMAINS = 'abc';

const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const OSM_ATTRIBUTION_TEXT = '© OpenStreetMap contributors';

const CARTO_ATTRIBUTION = `${OSM_ATTRIBUTION} &copy; <a href="https://carto.com/attributions">CARTO</a>`;
const CARTO_ATTRIBUTION_TEXT = '© OpenStreetMap contributors © CARTO';

/**
 * CARTO raster basemaps stamp an "API key required" watermark on every
 * unauthenticated tile, so they are only used when a key is configured
 * (REACT_APP_CARTO_BASEMAP_KEY). Without a key we fall back to the key-free
 * OpenStreetMap tiles.
 */
const cartoBasemapKey = process.env.REACT_APP_CARTO_BASEMAP_KEY || '';

export const hasCartoBasemapKey = Boolean(cartoBasemapKey);

const getCartoTileUrl = (style: string): string => {
  const params = new URLSearchParams({ key: cartoBasemapKey });

  return `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png?${params.toString()}`;
};

export const OSM_TILE_LAYER: TBasemapTileLayer = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: OSM_ATTRIBUTION,
  attributionText: OSM_ATTRIBUTION_TEXT,
  subdomains: DEFAULT_SUBDOMAINS,
  maxZoom: 19,
  supportsRetina: false,
};

export const TOPO_TILE_LAYER: TBasemapTileLayer = {
  url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
  attribution: `Map data: ${OSM_ATTRIBUTION}, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)`,
  attributionText: `${OSM_ATTRIBUTION_TEXT} © OpenTopoMap`,
  subdomains: DEFAULT_SUBDOMAINS,
  maxZoom: 17,
  supportsRetina: false,
};

const CARTO_LIGHT_TILE_LAYER: TBasemapTileLayer = {
  url: getCartoTileUrl('light_all'),
  attribution: CARTO_ATTRIBUTION,
  attributionText: CARTO_ATTRIBUTION_TEXT,
  subdomains: 'abcd',
  maxZoom: 20,
  supportsRetina: true,
};

const CARTO_DARK_TILE_LAYER: TBasemapTileLayer = {
  url: getCartoTileUrl('dark_all'),
  attribution: CARTO_ATTRIBUTION,
  attributionText: CARTO_ATTRIBUTION_TEXT,
  subdomains: 'abcd',
  maxZoom: 20,
  supportsRetina: true,
};

export const LIGHT_TILE_LAYER = hasCartoBasemapKey ? CARTO_LIGHT_TILE_LAYER : OSM_TILE_LAYER;

export const DARK_TILE_LAYER = hasCartoBasemapKey ? CARTO_DARK_TILE_LAYER : null;

type TBasemapTileUrlParams = {
  layer?: TBasemapTileLayer;
  zoom: number;
  x: number;
  y: number;
  tileIndex?: number;
  retina?: boolean;
};

/** Renders a leaflet tile template for consumers that fetch tiles themselves (canvas exports). */
export const getBasemapTileUrl = ({
  layer = LIGHT_TILE_LAYER,
  zoom,
  x,
  y,
  tileIndex = 0,
  retina = false,
}: TBasemapTileUrlParams): string => {
  const subdomains = layer.subdomains ? Array.from(layer.subdomains) : [];
  const tokens: Record<string, string> = {
    '{s}': subdomains.length ? subdomains[tileIndex % subdomains.length] : '',
    '{z}': String(zoom),
    '{x}': String(x),
    '{y}': String(y),
    '{r}': retina && layer.supportsRetina ? '@2x' : '',
  };

  return layer.url.replace(/{[srxyz]}/g, token => tokens[token] ?? token);
};
